import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  TextInput,
  RequiredFieldIndicator,
  Icon,
  Checkbox,
  useModal,
  connectModal,
  SelectDropdown,
} from '@gsa/afp-component-library';
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import AdditionalReqFilesTable from '../../../components/MultipleAdditionalRequirements/AdditionalReqFilesTable/AdditionalReqFilesTable';
import UploadNewFileModal from '../../../components/MultipleAdditionalRequirements/UploadNewFileModal/UploadNewFileModal';
import AttachmentDownload from '../../ReviewDetails/Attachment/AttachmentDownload/AttachmentDownload';
import './AreqVendorSection.scss';
import VehicleRequisitionContext from '../../../context/VehicleRequisitionContext/VehicleRequisitionContext';
import { VehicleRequisitionContextActions } from '../../../context/VehicleRequisitionContext/VehicleRequisitionContextActions';

const BLANK = { label: '-select-', value: '' };

const AreqVendorSection = ({
  draftId,
  index,
  indexChar,
  removeVendor,
  control,
  handleDelete,
  vendors,
  contractInfo,
}) => {
  const { dispatch } = useContext(VehicleRequisitionContext);

  const {
    setValue,
    formState: { errors },
  } = useFormContext();
  const uploadNewFileModal = useModal();
  const DisplayUploadFileModal = connectModal(UploadNewFileModal);

  const vendorQuoteDetails = useWatch({ name: 'vendorQuoteDetails' });
  useEffect(() => {
    dispatch({
      type: VehicleRequisitionContextActions.UPDATE_AREQ_VENDOR_QUOTES,
      payload: [...vendorQuoteDetails],
    });
  }, [vendorQuoteDetails]);

  const attachments = useWatch({
    name: `vendorQuoteDetails.${index}.attachments`,
  });
  const [showDocs, setShowDocs] = useState(Boolean(attachments?.length));

  const toggleShowDocs = () => {
    if (attachments?.length || !showDocs) setShowDocs(true);
    else setShowDocs(false);
  };

  const {
    fields: reqFiles,
    append,
    remove: reqRemove,
    update,
  } = useFieldArray({ name: `vendorQuoteDetails.${index}.attachments` });

  const { fields: quotesAttachment, append: quoteAppend } = useFieldArray({
    name: `vendorQuoteDetails.${index}.vendorQuote`,
  });

  const vendorId = useWatch({
    name: `vendorQuoteDetails.${index}.vendorId`,
  });

  const contractLineId = useWatch({
    name: `vendorQuoteDetails.${index}.contractLineId`,
  });

  const extendedShippingDaysWatch = useWatch({
    name: `vendorQuoteDetails.${index}.extendedShippingDays`,
  });

  const [makeModelOptions, setMakeModelOptions] = useState([BLANK]);
  const [contractLine, setContractLine] = useState({});
  const [totalShippingDays, setTotalShippingDays] = useState(0);
  const [baseShipmentDays, setBaseShipmentDays] = useState(0);
  const [additionalShippingDays, setAdditionalShippingDays] = useState(0);
  const [extendedShippingDays, setExtendedShippingDays] = useState(0);

  useEffect(() => {
    const newVendorContracts =
      contractInfo?.filter((x) => x.vendorId === vendorId) || [];
    setMakeModelOptions([
      BLANK,
      ...newVendorContracts.map((x) => ({
        label: x.makeModel,
        value: x.contractLineId,
      })),
    ]);
  }, [vendorId]);

  useEffect(() => {
    setExtendedShippingDays(+extendedShippingDaysWatch || 0);
  }, [extendedShippingDaysWatch]);

  useEffect(() => {
    const cl = contractInfo.find((x) => x.contractLineId === +contractLineId);
    setContractLine(cl);
    setBaseShipmentDays(cl?.baseShipmentDays || 0);
    setAdditionalShippingDays(cl?.additionalShippingDays || 0);
  }, [contractLineId]);

  useEffect(() => {
    setTotalShippingDays(
      baseShipmentDays + additionalShippingDays + extendedShippingDays,
    );
  }, [baseShipmentDays, additionalShippingDays, extendedShippingDays]);

  const errorsList = (errors?.vendorQuoteDetails?.length > index && errors?.vendorQuoteDetails[index]) || {};

  return (
    <section className="areq-vendor-section section-container vendor-quote-section">
      <div className="above-section-container">
        <div className="req-title">Vendor {indexChar}</div>
        {indexChar !== 'A' && (
          <div className="usa-button--unstyled delete-btn">
            <Icon className="usa-icon--size-2" iconName="close" />
            <Button
              data-testid="vendor-quote-delete-button"
              type="button"
              variant="unstyled"
              label="Delete vendor"
              onClick={() => {
                removeVendor(index);
              }}
            />
          </div>
        )}
      </div>
      <div className="form-section-container">
        <div role="tab" tabIndex="0" className="form-section">
          <Controller
            rules={{ required: 'This is a required field' }}
            name={`vendorQuoteDetails.${index}.vendorId`}
            render={({ field: { value, ref, onChange } }) => (
              <SelectDropdown
                name="vendorId"
                label={
                  <>
                    Vendor <RequiredFieldIndicator />
                  </>
                }
                id="vendorId"
                onChange={(e) => {
                  onChange(e);
                  setValue(`vendorQuoteDetails.${index}.contractLineId`, '');
                }}
                value={value}
                ref={ref}
                options={[BLANK, ...vendors]}
                errorMessage={
                  errors?.vendorQuoteDetails?.[index]?.vendorId?.message
                }
              />
            )}
          />

          <Controller
            rules={{ required: 'This is a required field' }}
            name={`vendorQuoteDetails.${index}.contractLineId`}
            render={({ field: { value, onChange, ref } }) => (
              <SelectDropdown
                name="contractLineId"
                label={
                  <>
                    Make / Model <RequiredFieldIndicator />
                  </>
                }
                id="contractLineId"
                onChange={onChange}
                value={value}
                ref={ref}
                options={makeModelOptions}
                errorMessage={
                  errors?.vendorQuoteDetails?.[index]?.contractLineId?.message
                }
              />
            )}
          />

          <div className="quote-upload-section">
            <div
              className={
                errorsList?.vendorQuote?.root?.message &&
                'usa-form-group usa-form-group--error'
              }
            >
              <Controller
                rules={{ required: 'An uploaded file is required' }}
                name={`vendorQuoteDetails.${index}.vendorQuote`}
                control={control}
                render={({ field: { value } }) => (
                  <div
                    className={`upload-vendor-quote ${
                      errorsList?.vendorQuote?.root?.message &&
                      'usa-input-error'
                    }`}
                  >
                    <div className="upload-section-title">
                      Upload vendor quote
                      <RequiredFieldIndicator />
                    </div>
                    <div className="upload-section-desc" />
                    <span className="usa-error-message">
                      {errorsList?.vendorQuote?.root?.message}
                    </span>

                    <div className="uploaded_quote_section">
                      <div>{quotesAttachment[0]?.name}</div>
                      {quotesAttachment[0]?.name && (
                        <div className="quote-attach-button">
                          <AttachmentDownload
                            name="View"
                            metadataId={value[0]?.metadataId}
                          />
                          <Button
                            onClick={() => {
                              handleDelete(index);
                            }}
                            type="button"
                            variant="unstyled"
                            label="Delete"
                          />
                        </div>
                      )}
                    </div>

                    {!quotesAttachment[0]?.name && (
                      <Button
                        onClick={() => {
                          uploadNewFileModal.openModal();
                        }}
                        type="button"
                        variant="outline"
                        leftIcon={{ name: 'add', className: 'plus-icon' }}
                        label="Upload file"
                      />
                    )}
                  </div>
                )}
              />
            </div>
          </div>

          <div className="vendor-quote-description-textarea">
            <Controller
              name={`vendorQuoteDetails.${index}.description`}
              render={({ field: { value, onChange, ref } }) => (
                <TextInput
                  characterLimit={100}
                  type="textarea"
                  label={<>Contracting officer&apos;s comments</>}
                  onChange={onChange}
                  value={value}
                  inputRef={ref}
                  name={`vendorQuoteDetails.${index}.description`}
                  errorMessage={
                    errors?.vendorQuoteDetails?.[index]?.description?.message
                  }
                />
              )}
            />
          </div>

          <Checkbox
            className="supporting-docs-checkbox"
            name={`vendorQuoteDetails.${index}.supportingDocs`}
            checked={showDocs}
            onChange={toggleShowDocs}
            label="Supporting documents"
          />

          {showDocs && (
            <AdditionalReqFilesTable
              reqFiles={reqFiles}
              append={append}
              remove={reqRemove}
              update={update}
              additionalRequirementIndex={index}
              draftId={draftId}
              uploadSectionType="VENDOR_QUOTE"
            />
          )}

          <Controller
            rules={{
              required: 'This is a required field',
            }}
            name={`vendorQuoteDetails.${index}.priceEstimate`}
            render={({ field: { value, onChange, ref } }) => (
              <TextInput
                type="number"
                label="AREQ price per vehicle"
                onChange={onChange}
                value={value}
                prefix="$"
                inputRef={ref}
                required
                name={`vendorQuoteDetails.${index}.priceEstimate`}
                errorMessage={
                  errors?.vendorQuoteDetails?.[index]?.priceEstimate?.message
                }
              />
            )}
          />

          <div className="shipping-days-section">
            <div className="additional-shipment-days-increase-input">
              <Controller
                name={`vendorQuoteDetails.${index}.extendedShippingDays`}
                render={({ field: { value, onChange, ref } }) => (
                  <TextInput
                    type="number"
                    label="Extended shipping days"
                    onChange={onChange}
                    value={value}
                    inputRef={ref}
                    name={`vendorQuoteDetails.${index}.extendedShippingDays`}
                    errorMessage={
                      errors?.vendorQuoteDetails?.[index]?.extendedShippingDays
                        ?.message
                    }
                  />
                )}
              />
            </div>

            <div className="shipping-days-summary-section">
              <div className="shipping-days-title">Shipping Days</div>
              <div className="shipping-data-section">
                <div>Base:</div>
                <div>{`${contractLine ? baseShipmentDays : '--'}`}</div>
              </div>
              <div className="shipping-data-section">
                <div>Additional:</div>
                <div>{`${contractLine ? additionalShippingDays : '--'}`}</div>
              </div>
              <div className="shipping-data-section">
                <div>Extended:</div>
                <div>{`${contractLine ? extendedShippingDays : '--'}`}</div>
              </div>
              <div className="shipping-data-section shipping-total">
                <div>Total:</div>
                <div>{`${contractLine ? totalShippingDays : '--'}`}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <DisplayUploadFileModal
        isOpen={uploadNewFileModal.isOpen}
        handleClose={uploadNewFileModal.closeModal}
        append={quoteAppend}
        reqType="URGENT_REQ_VENDOR_QUOTES"
        draftId={draftId}
      />
    </section>
  );
};

AreqVendorSection.propTypes = {
  draftId: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  indexChar: PropTypes.string.isRequired,
  removeVendor: PropTypes.func.isRequired,
  control: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  vendors: PropTypes.instanceOf(Array).isRequired,
  contractInfo: PropTypes.instanceOf(Array).isRequired,
};

export default AreqVendorSection;
